import { NextAction, Urgency } from '../priority.js';
import { AgendaSlot } from './agenda.js';
import { z } from 'zod';
import { MaintenanceCategory } from '../maintenance.js';
import { PlateKind, normalizePlate } from '../plate.js';
import { StorageTerms } from '../storage-terms.js';
import {
  ActorType,
  DecisionValue,
  Email,
  Id,
  InterventionStatus,
  LineKind,
  LongText,
  Money,
  PhoneE164,
  queryList,
  Quantity,
  ShortText,
  Timestamp,
  VatRate,
  amount,
} from './common.js';
import { PhotoConsentOutput } from './privacy.js';
import { queryBool } from './inbox.js';

// ─── Photos de fiche (vague 4) ───────────────────────────────────────────────

/**
 * Photo de la fiche d'un véhicule ou d'un client (table `profile_photos`, hors intervention).
 * `thumbUrl` : chemin relatif de l'API (`profilePhotos.file`, après contrôle), jamais un fichier public.
 * La photo d'un client est interne au garage : jamais sur le portail ni dans un e-mail.
 */
export const ProfilePhotoOutput = z.object({ id: Id, thumbUrl: z.string() });
export type ProfilePhotoOutput = z.infer<typeof ProfilePhotoOutput>;
/** Champs texte du formulaire multipart (le fichier est la partie `file`) : aucun. */
export const ProfilePhotoUploadFields = z.strictObject({});
export const ProfilePhotoFileQuery = z.strictObject({ variant: z.enum(['thumb', 'display']).default('thumb') });

// ─── Remarques des fiches (02/10/2026) ───────────────────────────────────────

/** Longueur maximale d'une remarque de fiche client ou véhicule (contrainte CHECK identique en base). */
export const RECORD_NOTES_MAX = 2000;
/**
 * Remarque d'une fiche client ou véhicule : texte libre **interne au garage** (jamais au portail ni dans un
 * e-mail), retrouvé par la « Base garage » (`directory.search`). Espaces de bord retirés ; une saisie vide
 * vaut `null` (remarque supprimée).
 */
export const RecordNotes = z
  .string()
  .trim()
  .max(RECORD_NOTES_MAX, `La remarque ne peut pas dépasser ${RECORD_NOTES_MAX} caractères`)
  .transform((v) => (v === '' ? null : v));

// ─── Clients ─────────────────────────────────────────────────────────────────

export const Civility = z.enum(['mme', 'm']);
/** Personne (prénom, nom) ou société (raison sociale) : vague 4. */
export const CustomerKind = z.enum(['person', 'company']);
export type CustomerKind = z.infer<typeof CustomerKind>;

export const CustomerOutput = z.object({
  id: Id,
  /** Nom d'affichage, calculé par la base : « Prénom Nom » ou raison sociale. */
  name: z.string(),
  kind: CustomerKind,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /**
   * Civilité pour les e-mails (« Bonjour Madame Durand ») ; `null` = « Bonjour, ».
   * Ignorée pour une société (« Bonjour, »).
   */
  civility: Civility.nullable(),
  /** Photo de fiche (interne au garage), ou `null` (vague 4). */
  photo: ProfilePhotoOutput.nullable(),
  /** Remarque interne au garage (02/10/2026), `null` sans remarque. Obligatoire depuis l'intégration de la vague 7. */
  notes: z.string().nullable(),
  createdAt: Timestamp,
  /**
   * Vague 6 : opposition aux e-mails (aucun e-mail au client, `customers.email_opt_out_at`), accord du
   * client pour sa photo (date et version du texte lu), fiche effacée (anonymisée) le … : renseignés par
   * `customerPrivacyFields` du cœur. Obligatoires depuis l'intégration de la vague 7 (02/10/2026).
   */
  emailOptOut: z.boolean(),
  photoConsent: PhotoConsentOutput.nullable(),
  erasedAt: Timestamp.nullable(),
});

/**
 * Objet de base NON raffiné (Zod 4 interdit `.extend()`/`.partial()` sur un objet raffiné) :
 * chaque schéma dérivé applique lui-même `customerIdentityRefinement`.
 */
const CustomerBase = z.strictObject({
  kind: CustomerKind,
  firstName: ShortText.nullable().optional(),
  lastName: ShortText.nullable().optional(),
  companyName: ShortText.nullable().optional(),
  email: Email.nullable().optional(),
  phone: PhoneE164.nullable().optional(),
  civility: Civility.nullable().optional(),
  /** Vague 6 : « ne plus recevoir d'e-mails » (opposition, RGPD art. 21). */
  emailOptOut: z.boolean().optional(),
  /** Remarque interne au garage (02/10/2026) ; `null` ou `''` la supprime. */
  notes: RecordNotes.nullable().optional(),
});

const MISSING_LAST_NAME = 'Le nom est obligatoire pour une personne';
const MISSING_COMPANY_NAME = 'La raison sociale est obligatoire pour une société';

interface CustomerIdentity {
  kind?: CustomerKind | undefined;
  lastName?: string | null | undefined;
  companyName?: string | null | undefined;
}

/**
 * Règle d'identité sur un état complet (création, ou fiche actuelle fusionnée avec une mise à jour) :
 * personne ⇒ nom non vide ; société ⇒ raison sociale non vide. Renvoie les erreurs par champ.
 * L'API l'applique après fusion lors d'une mise à jour partielle (refus 422).
 */
export function customerIdentityIssues(c: CustomerIdentity): { path: 'lastName' | 'companyName'; message: string }[] {
  const kind = c.kind ?? 'person';
  if (kind === 'person' && !c.lastName?.trim()) return [{ path: 'lastName', message: MISSING_LAST_NAME }];
  if (kind === 'company' && !c.companyName?.trim()) return [{ path: 'companyName', message: MISSING_COMPANY_NAME }];
  return [];
}

/** Création : `kind` vaut `person` par défaut ; nom (personne) ou raison sociale (société) exigé. */
export const CustomerInput = CustomerBase.extend({ kind: CustomerKind.default('person') }).superRefine((c, ctx) => {
  for (const issue of customerIdentityIssues(c)) {
    ctx.addIssue({ code: 'custom', path: [issue.path], message: issue.message });
  }
});

/**
 * Mise à jour partielle : la règle ne porte que sur les champs fournis.
 * - `lastName: null` est refusé si `kind` fourni vaut `person` ; `companyName: null` si `kind` vaut `company` ;
 * - si `kind` est fourni, le champ d'identité correspondant doit l'être aussi (changement de nature explicite).
 * La cohérence avec la fiche existante (champ absent) est vérifiée par l'API après fusion
 * (`customerIdentityIssues`), en plus de la contrainte CHECK de la base.
 */
export const CustomerUpdateInput = CustomerBase.partial().superRefine((c, ctx) => {
  if (c.kind === 'person' && !c.lastName?.trim()) {
    ctx.addIssue({ code: 'custom', path: ['lastName'], message: MISSING_LAST_NAME });
  }
  if (c.kind === 'company' && !c.companyName?.trim()) {
    ctx.addIssue({ code: 'custom', path: ['companyName'], message: MISSING_COMPANY_NAME });
  }
  if (c.kind === undefined && c.lastName === null && c.companyName === null) {
    ctx.addIssue({ code: 'custom', path: ['lastName'], message: 'Le nom ou la raison sociale est obligatoire' });
  }
});
export const SearchQuery = z.strictObject({ q: z.string().trim().max(100).optional() });
/** Véhicules : recherche, ou véhicules d'un client précis (défaut D-L8-1 : plus de filtre dans le navigateur sur 100 lignes). */
export const VehicleListQuery = z.strictObject({ q: z.string().trim().max(100).optional(), customerId: Id.optional() });

// ─── Véhicules ───────────────────────────────────────────────────────────────

/** Plaque brute en entrée : normalisée selon `plateKind` par les schémas de véhicule (`normalizePlate`). */
export const Plate = z.string().trim().min(2).max(30);
export const Vin = z
  .string()
  .trim()
  .regex(/^[A-HJ-NPR-Z0-9]{11,17}$/i, 'VIN invalide')
  .transform((v) => v.toUpperCase());

export const VehicleOutput = z.object({
  id: Id,
  customerId: Id,
  plate: z.string(),
  plateKind: PlateKind,
  make: z.string(),
  model: z.string(),
  version: z.string().nullable(),
  vin: z.string().nullable(),
  /** Photo de fiche, ou `null` (vague 4). */
  photo: ProfilePhotoOutput.nullable(),
  /** Remarque interne au garage (02/10/2026), `null` sans remarque ; voir `CustomerOutput.notes`. */
  notes: z.string().nullable(),
});

/** Objet de base NON raffiné (voir `CustomerBase`). `make`/`model` : texte libre, le catalogue n'est qu'une aide. */
const VehicleBase = z.strictObject({
  plate: Plate,
  /** `fr` par défaut (plaque française contrôlée) ; `other` = plaque étrangère ou spéciale. */
  plateKind: PlateKind.optional(),
  make: ShortText,
  model: ShortText,
  version: ShortText.nullable().optional(),
  vin: Vin.nullable().optional(),
  /** Remarque interne au garage (02/10/2026) ; `null` ou `''` la supprime. */
  notes: RecordNotes.nullable().optional(),
});

/**
 * Normalise la plaque fournie (et fixe `plateKind`, `fr` par défaut) ou ajoute une erreur sur `plate`.
 * Sans `plate`, l'objet est rendu tel quel (mise à jour de `plateKind` seul : revalidée par l'API).
 */
function withNormalizedPlate<T extends { plate?: string | undefined; plateKind?: PlateKind | undefined }>(
  v: T,
  ctx: z.RefinementCtx,
): T {
  if (v.plate === undefined) return v;
  const kind = v.plateKind ?? 'fr';
  const result = normalizePlate(kind, v.plate);
  if (!result.ok) {
    ctx.addIssue({ code: 'custom', path: ['plate'], message: result.message });
    return z.NEVER;
  }
  return { ...v, plate: result.value, plateKind: kind };
}

/** Véhicule créé à l'entrée (`CreateInterventionInput.vehicle.new`). Sortie : `plate` normalisée, `plateKind` fixé. */
export const VehicleFields = VehicleBase.transform((v, ctx) => withNormalizedPlate(v, ctx) as typeof v & { plateKind: PlateKind });
export const VehicleInput = VehicleBase.extend({ customerId: Id }).transform(
  (v, ctx) => withNormalizedPlate(v, ctx) as typeof v & { plateKind: PlateKind },
);
/**
 * Mise à jour partielle. `plate` fourni sans `plateKind` ⇒ `fr`. `plateKind` seul ⇒ l'API revalide la
 * plaque actuelle avec le nouveau type (`normalizePlate`) et enregistre la valeur normalisée, sinon 422.
 */
export const VehicleUpdateInput = VehicleBase.extend({ customerId: Id }).partial().transform(withNormalizedPlate);

// ─── Travaux réalisés ────────────────────────────────────────────────────────

export const WorkItemOutput = z.object({
  id: Id,
  interventionId: Id,
  maintenanceCategory: MaintenanceCategory,
  kind: LineKind,
  label: z.string(),
  reference: z.string().nullable(),
  quantity: Quantity,
  unitPriceHt: amount(),
  vatRate: z.string(),
  totalHt: amount(),
  totalTtc: amount(),
  complementId: Id.nullable(),
  performedAt: Timestamp,
});
export type WorkItemOutput = z.infer<typeof WorkItemOutput>;

/** Prix facultatif : un mécanicien sans `amounts.view` saisit la pièce, l'accueil complète le prix. */
export const WorkItemInput = z.strictObject({
  maintenanceCategory: MaintenanceCategory,
  kind: LineKind,
  label: ShortText,
  reference: ShortText.nullable().optional(),
  quantity: Quantity,
  unitPriceHt: Money.nullable().optional(),
  vatRate: VatRate.optional(),
  complementId: Id.nullable().optional(),
});
export const WorkItemUpdateInput = WorkItemInput.partial();

// ─── Historique d'un véhicule chez ce garage ────────────────────────────────

export const HistoryEntry = z.object({
  interventionId: Id,
  receivedAt: Timestamp,
  releasedAt: Timestamp.nullable(),
  entryMileage: z.int(),
  exitMileage: z.int().nullable(),
  customerRequest: z.string(),
  workItems: z.array(WorkItemOutput),
  totalTtc: amount(),
});
export type HistoryEntry = z.infer<typeof HistoryEntry>;

// ─── Interventions ───────────────────────────────────────────────────────────

export const InterventionSummary = z.object({
  id: Id,
  status: InterventionStatus,
  /** `version` et vignette `photo` : vue Parc et listes (vague 4). */
  vehicle: z.object({
    id: Id,
    plate: z.string(),
    make: z.string(),
    model: z.string(),
    version: z.string().nullable(),
    photo: ProfilePhotoOutput.nullable(),
  }),
  customer: z.object({ id: Id, name: z.string() }),
  location: z.object({ id: Id, name: z.string() }).nullable(),
  assignedMembershipId: Id.nullable(),
  receivedAt: Timestamp,
  readyAt: Timestamp.nullable(),
  freeUntil: Timestamp.nullable(),
  updatedAt: Timestamp,
  pending: z.object({
    openQuestions: z.int(),
    decisionsReceived: z.int(),
    complementsToValidate: z.int(),
    /** Vague 6 : versions envoyées qui ont reçu une demande de modification (statut `change_requested`). */
    changeRequests: z.int(),
  }),
  /**
   * Vague 6 : état du gardiennage pour les indicateurs (aucun montant) : `none` (pas prêt ou exonéré),
   * `running` (délai gratuit en cours), `due_soon` (fin dans moins de `STORAGE_DUE_SOON_HOURS`), `overdue`.
   */
  storageState: z.enum(['none', 'running', 'due_soon', 'overdue']),
  /**
   * Vague 7 : indicateurs de priorité, calculés par le serveur (`daysOnSite`, `interventionUrgency`,
   * `nextActionFor` de `priority.ts`, droits du membre) ; créneau d'agenda du dossier (`null` sans créneau).
   * Obligatoires depuis l'intégration de la vague 7 (02/10/2026).
   */
  daysOnSite: z.int().nonnegative(),
  urgency: Urgency,
  nextAction: NextAction,
  agendaSlot: AgendaSlot.nullable(),
  /**
   * 02/10/2026 : **dernière action** sur le dossier (ISO UTC) : la plus récente écriture significative de
   * l'équipe ou du client (journal d'audit lié au dossier : statut, travaux, photos, messages, compléments,
   * décisions, fiche de réception, liens portail), hors lectures et hors tâches automatiques (rappels,
   * e-mails du worker) ; date de création du dossier à défaut. `idleDays` : jours entiers dans le fuseau du
   * garage depuis `lastActivityAt` (`idleDays` de `priority.ts`). `idle` : dossier non clôturé et
   * `idleDays >= idleAfterDays` (réglage du garage, `isIdle`). Obligatoires depuis l'intégration de la vague 7.
   */
  lastActivityAt: Timestamp,
  idleDays: z.int().nonnegative(),
  idle: z.boolean(),
  /**
   * 03/10/2026 : dossier **épinglé par le membre courant** (épingle personnelle, `interventions.pin`) ;
   * toujours `false` pour une session support (aucune appartenance).
   */
  pinned: z.boolean(),
});
export type InterventionSummary = z.infer<typeof InterventionSummary>;

/** Vague 6 : « délai gratuit bientôt dépassé » = fin dans moins de 24 heures. */
export const STORAGE_DUE_SOON_HOURS = 24;

/** Vague 6 : statuts d'un véhicule présent, dans l'ordre du flux de l'atelier (colonnes de l'Accueil). */
export const WORKSHOP_STATUS_FLOW = ['received', 'in_progress', 'awaiting_decision', 'awaiting_parts', 'ready'] as const;

/**
 * Vague 6 : compteurs de l'Accueil, calculés par le serveur pour le garage actif et selon les droits du
 * membre (aucun montant). `messagesToAnswer` : messages du client non traités ; `unreadForMe` : non-lus du
 * membre (messagerie centrale) ; `storageOverdue` : véhicules prêts dont le délai gratuit est dépassé.
 */
export const HomeCounters = z.object({
  vehiclesOnSite: z.int().nonnegative(),
  byStatus: z.object({
    received: z.int().nonnegative(),
    in_progress: z.int().nonnegative(),
    awaiting_decision: z.int().nonnegative(),
    awaiting_parts: z.int().nonnegative(),
    ready: z.int().nonnegative(),
  }),
  messagesToAnswer: z.int().nonnegative(),
  unreadForMe: z.int().nonnegative(),
  decisionsReceived: z.int().nonnegative(),
  changeRequests: z.int().nonnegative(),
  complementsToValidate: z.int().nonnegative(),
  readyAwaitingPickup: z.int().nonnegative(),
  storageOverdue: z.int().nonnegative(),
  /** Vague 6 (ajout 11) : arrivées prévues aujourd'hui, et planifiés en retard. */
  plannedToday: z.int().nonnegative(),
  plannedOverdue: z.int().nonnegative(),
});
export type HomeCounters = z.infer<typeof HomeCounters>;

/** Prochaine échéance du compte rendu (08/10/2026) : libellé, date civile `AAAA-MM-JJ`, kilométrage. */
export const NextDueOutput = z.object({ label: z.string().nullable(), date: z.string().nullable(), mileage: z.int().nullable() });
export type NextDueOutput = z.infer<typeof NextDueOutput>;

/**
 * 08/10/2026 : rappel automatique de la prochaine échéance (module `maintenance_reminders`), calculé par le
 * serveur. `scheduled` : e-mail prévu le `date` (jour du garage) ; `sent` : envoyé à `at` ; `not_sent` : rappel
 * traité sans envoi (`reason` : opposition, client effacé, garage suspendu…) ; `mileage_only` : échéance au
 * kilométrage seul, jamais d'envoi automatique ; `no_email` : client sans adresse ; `past` : date dépassée
 * sans rappel ; `disabled` : module désactivé.
 */
export const NextDueReminderState = z.enum(['scheduled', 'sent', 'not_sent', 'mileage_only', 'no_email', 'past', 'disabled']);
export type NextDueReminderState = z.infer<typeof NextDueReminderState>;
export const NextDueReminder = z.object({
  state: NextDueReminderState,
  date: z.string().nullable(),
  at: Timestamp.nullable(),
  reason: z.string().nullable(),
});
export type NextDueReminder = z.infer<typeof NextDueReminder>;

export const InterventionDetail = InterventionSummary.extend({
  entryMileage: z.int(),
  customerRequest: z.string(),
  entryConditionNotes: z.string().nullable(),
  termsAcceptedAt: Timestamp,
  storageTerms: StorageTerms,
  storageWaiver: z.object({ at: Timestamp, reason: z.string(), byName: z.string().nullable() }).nullable(),
  releasedAt: Timestamp.nullable(),
  exitMileage: z.int().nullable(),
  closedAt: Timestamp.nullable(),
  /** Nombre de réouvertures après clôture (travaux complémentaires). */
  reopenCount: z.int(),
  lastReopenedAt: Timestamp.nullable(),
  /** Kilométrage relevé à la dernière réouverture (n'écrase jamais celui de l'entrée). */
  lastReopenMileage: z.int().nullable(),
  /**
   * Portail en cours (02/10/2026) : récapitulatif des travaux prévus, rédigé par le garage (distinct de
   * `customerRequest`, la demande du client) et **montant estimé TTC**, indicatif et non contractuel (la
   * facture reste dans Pennylane). `null` si non renseignés. Le montant est retiré sans `amounts.view`.
   * Obligatoire depuis l'intégration de la vague 7 (`null` sans récapitulatif).
   */
  plannedWork: z.string().nullable(),
  estimatedTotalTtc: amount(),
  /** Compte rendu de revente (08/10/2026) : préconisations et prochaine échéance (`null` si absentes). */
  recommendations: z.string().nullable().optional(),
  nextDue: NextDueOutput.nullable().optional(),
  /** 08/10/2026 : état du rappel de la prochaine échéance (`null` sans échéance). */
  nextDueReminder: NextDueReminder.nullable().optional(),
});
export type InterventionDetail = z.infer<typeof InterventionDetail>;

export const InterventionListQuery = z.strictObject({
  scope: z.enum(['present', 'all']).default('present'),
  /** Recherche (plaque, client, marque, modèle) : retrouve aussi les dossiers clôturés avec scope=all (audit C5). */
  q: z.string().trim().max(100).optional(),
  // ─── Filtres de la vue Parc (vague 4), cumulables, côté serveur ───
  /** Marques : comparaison sans casse ni accents (`app_fold` en SQL, `foldText` en JS). Clé répétée. */
  make: queryList(ShortText, 20).optional(),
  model: queryList(ShortText, 50).optional(),
  status: queryList(InterventionStatus, 6).optional(),
  locationId: queryList(Id, 50).optional(),
  assignedMembershipId: queryList(Id, 50).optional(),
  /** Jours d'entrée dans le fuseau du garage, bornes incluses. */
  receivedFrom: z.iso.date().optional(),
  receivedTo: z.iso.date().optional(),
  /**
   * Vague 7 : `recent` (défaut : entrée la plus récente d'abord) ou `priority` (`comparePriority` : urgence,
   * jours à l'atelier, entrée la plus ancienne).
   */
  sort: z.enum(['recent', 'priority']).default('recent'),
  /**
   * 02/10/2026 : `idle=1` (ou `true`) ne garde que les dossiers « sans action » (`idle: true` : non clôturés, sans action
   * depuis `idleAfterDays` jours ou plus). Filtre côté serveur, cumulable avec les autres ; `0` ou absent : aucun
   * filtre.
   */
  idle: queryBool.optional(),
  /** 03/10/2026 : `pinned=1` ne garde que les dossiers épinglés par le membre courant (cumulable avec `scope`). */
  pinned: queryBool.optional(),
});

/** 03/10/2026 : réponse de `interventions.pin` / `interventions.unpin` (état après l'appel, idempotent). */
export const InterventionPinOutput = z.object({ interventionId: Id, pinned: z.boolean() });
export type InterventionPinOutput = z.infer<typeof InterventionPinOutput>;

/** Facettes du parc : uniquement les valeurs présentes dans le garage actif, sur le même `scope`. */
export const InterventionFacetsQuery = z.strictObject({ scope: z.enum(['present', 'all']).default('present') });
const FacetCount = z.int().min(0);
export const InterventionFacets = z.object({
  makes: z.array(
    z.object({
      value: z.string(),
      count: FacetCount,
      models: z.array(z.object({ value: z.string(), count: FacetCount })),
    }),
  ),
  statuses: z.array(z.object({ value: InterventionStatus, count: FacetCount })),
  locations: z.array(z.object({ id: Id, name: z.string(), count: FacetCount })),
  assignees: z.array(z.object({ membershipId: Id, name: z.string(), count: FacetCount })),
});
export type InterventionFacets = z.infer<typeof InterventionFacets>;

export const Mileage = z.int().min(0).max(5_000_000);

/**
 * Récapitulatif des travaux prévus (02/10/2026) : texte libre du garage, montré au client sur le portail,
 * 2 000 caractères au plus, jamais vide (`null` pour l'effacer).
 */
export const PlannedWork = LongText.min(1).max(2000);

export const CreateInterventionInput = z.strictObject({
  customer: z.union([z.strictObject({ id: Id }), z.strictObject({ new: CustomerInput })]),
  vehicle: z.union([z.strictObject({ id: Id }), z.strictObject({ new: VehicleFields })]),
  entryMileage: Mileage,
  customerRequest: LongText.min(1),
  entryConditionNotes: LongText.nullable().optional(),
  locationId: Id.nullable().optional(),
  assignedMembershipId: Id.nullable().optional(),
  /** Case cochée : conditions de gardiennage remises au client. */
  termsAccepted: z.literal(true),
  /**
   * Date d'entrée (vague 4). Absente : maintenant (horloge du serveur). Fournie : dans
   * `[maintenant − 30 jours, maintenant + 5 minutes]`, sinon 422 (`checkReceivedAt`, contrôle par l'API).
   */
  receivedAt: Timestamp.optional(),
  /** Récapitulatif des travaux prévus (voir `InterventionDetail.plannedWork`), montré au client. */
  plannedWork: PlannedWork.nullable().optional(),
  /** Montant estimé TTC, indicatif : exige `amounts.view` (403 sinon). */
  estimatedTotalTtc: Money.nullable().optional(),
});

export const UpdateInterventionInput = z.strictObject({
  /** Depuis « prêt », revenir à un état en cours annule la mise à disposition (readyAt, freeUntil). */
  status: z.enum(['received', 'in_progress', 'awaiting_decision', 'awaiting_parts']).optional(),
  locationId: Id.nullable().optional(),
  assignedMembershipId: Id.nullable().optional(),
  customerRequest: LongText.min(1).optional(),
  entryConditionNotes: LongText.nullable().optional(),
  /**
   * Correction de la date d'entrée (vague 4) : dans `[createdAt − 30 jours, min(maintenant, readyAt, releasedAt)]`
   * (`receivedAtBoundsForUpdate`), refusée sur un dossier clôturé, tracée au journal (ancienne et nouvelle valeur).
   */
  receivedAt: Timestamp.optional(),
  /**
   * Vague 7 : créneau d'agenda du dossier (module `agenda` actif, sinon 403 `module_disabled`) ; `null`
   * l'efface. Audité (`agendaSlot`).
   */
  agendaSlot: AgendaSlot.nullable().optional(),
  /** Récapitulatif des travaux prévus ; `null` l'efface. Audité (avant / après). */
  plannedWork: PlannedWork.nullable().optional(),
  /**
   * Montant estimé TTC, indicatif ; `null` l'efface. Exige `amounts.view` (403 sinon : on ne fixe ni
   * n'efface un montant qu'on ne voit pas). Audité (avant / après, retiré du journal sans `amounts.view`).
   */
  estimatedTotalTtc: Money.nullable().optional(),
});

// ─── Compte rendu de revente (08/10/2026) ───────────────────────────────────

/** Préconisations du garage (texte libre, montré au client et dans le PDF) ; `null` pour les effacer. */
export const Recommendations = LongText.min(1).max(2000);

/** Date civile `AAAA-MM-JJ` (sans heure ni fuseau), valide au calendrier. */
export const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
  .refine((v) => {
    const d = new Date(`${v}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, 'Date invalide');

/**
 * Prochaine échéance (ex. « Prochaine vidange » à 35 000 km ou en mars 2027) : libellé facultatif, date
 * et/ou kilométrage (au moins l'un des deux). Indicative, jamais un engagement.
 */
export const NextDueInput = z
  .strictObject({
    label: z.string().trim().min(1).max(120).nullable().default(null),
    date: CalendarDate.nullable().default(null),
    mileage: Mileage.nullable().default(null),
  })
  .refine((d) => d.date !== null || d.mileage !== null, { message: 'Indiquer une date ou un kilométrage', path: ['date'] });

/** Champs du compte rendu saisis à la restitution et après (`releaseReports.update`) ; absents = inchangés. */
export const ReleaseReportFieldsInput = z.strictObject({
  recommendations: Recommendations.nullable().optional(),
  nextDue: NextDueInput.nullable().optional(),
});
export type ReleaseReportFieldsInput = z.infer<typeof ReleaseReportFieldsInput>;

/** PDF du compte rendu côté garage : `amounts=true` (droit `amounts.view` exigé, 403 sinon) ajoute les montants. */
export const ReleaseReportPdfQuery = z.strictObject({ amounts: z.enum(['true', 'false']).optional() });

export const ReleaseInput = z.strictObject({
  exitMileage: Mileage,
  recommendations: Recommendations.nullable().optional(),
  nextDue: NextDueInput.nullable().optional(),
});
export const StorageWaiverInput = z.strictObject({ reason: ShortText.min(3) });

/**
 * Réouverture d'un dossier clôturé (travaux complémentaires après la sortie) : le véhicule revient,
 * le dossier repasse en cours ; nouveaux compléments, travaux, mise à disposition et échéance possibles.
 */
export const ReopenInput = z.strictObject({ reason: ShortText.min(3), entryMileage: Mileage.optional() });

export const VehicleDetailOutput = z.object({
  vehicle: VehicleOutput,
  customer: CustomerOutput,
  history: z.array(HistoryEntry),
});

// ─── Accueil par rôle et journal ─────────────────────────────────────────────

// ─── Vague 6 : intervention planifiée (ajout 11) ─────────────────────────────

/** Créneau facultatif : matin, après-midi, ou heure « HH:MM » (fuseau du garage). */
export const PlannedSlot = z.union([z.enum(['morning', 'afternoon']), z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)]);
export const PlannedStatus = z.enum(['planned', 'checked_in', 'cancelled']);
export type PlannedStatus = z.infer<typeof PlannedStatus>;

/**
 * Intervention planifiée : **jamais** un véhicule présent (table `planned_interventions`, distincte de
 * `interventions` : aucun kilométrage, aucune condition figée, aucun gardiennage). L'« Entrée véhicule »
 * (`interventions.checkIn`) crée l'intervention `received`.
 */
export const PlannedInterventionOutput = z.object({
  id: Id,
  status: PlannedStatus,
  plannedFor: z.iso.date(),
  slot: PlannedSlot.nullable(),
  estimatedMinutes: z.int().nullable(),
  customerRequest: z.string(),
  /** Intégration vague 7 : travaux prévus et montant estimé TTC du rendez-vous (`null` sinon ; montant retiré sans `amounts.view`). */
  plannedWork: z.string().nullable(),
  estimatedTotalTtc: amount(),
  vehicle: z.object({ id: Id, plate: z.string(), make: z.string(), model: z.string() }),
  customer: z.object({ id: Id, name: z.string() }),
  location: z.object({ id: Id, name: z.string() }).nullable(),
  assignedMembershipId: Id.nullable(),
  /** Date prévue dépassée et véhicule non arrivé. */
  overdue: z.boolean(),
  /** Intervention créée à l'entrée, sinon `null`. */
  interventionId: Id.nullable(),
  cancelledAt: Timestamp.nullable(),
  cancelReason: z.string().nullable(),
  createdAt: Timestamp,
});
export type PlannedInterventionOutput = z.infer<typeof PlannedInterventionOutput>;

/** Accueil : arrivées prévues (fuseau du garage) et planifiés en retard (date dépassée, véhicule non arrivé). */
export const HomePlannedArrivals = z.object({
  today: z.array(PlannedInterventionOutput),
  tomorrow: z.array(PlannedInterventionOutput),
  /** Du surlendemain à la fin de la semaine (dimanche) ; vide le samedi et le dimanche. */
  thisWeek: z.array(PlannedInterventionOutput),
  overdue: z.array(PlannedInterventionOutput),
});

export const HomeOutput = z.object({
  interventions: z.array(InterventionSummary),
  openQuestions: z.array(
    z.object({ interventionId: Id, messageId: Id, excerpt: z.string(), createdAt: Timestamp }),
  ),
  decisionsReceived: z.array(
    z.object({
      interventionId: Id,
      complementId: Id,
      title: z.string(),
      decision: DecisionValue,
      decidedAt: Timestamp,
    }),
  ),
  complementsToValidate: z.array(z.object({ interventionId: Id, complementId: Id, title: z.string() })),
  /**
   * Vague 6 : « Modification demandée » : compléments dont la version envoyée a reçu une demande de
   * modification (statut `change_requested`), à réviser ou annuler. Jamais dans `decisionsReceived`.
   */
  changesRequested: z
    .array(
      z.object({
        interventionId: Id,
        complementId: Id,
        title: z.string(),
        version: z.int(),
        decidedAt: Timestamp,
      }),
    )
    .optional(),
  /** Vague 6 : tableau de bord ; champs `counters`, `parc`, `plannedArrivals`, `changesRequested` facultatif pendant la vague 6 (lots en cours) ; rendu obligatoire par L0 à l’intégration. */
  counters: HomeCounters.optional(),
  /**
   * Vague 6 : véhicules présents groupés par statut, dans l'ordre `WORKSHOP_STATUS_FLOW` (un groupe par
   * statut, même vide), avec leurs indicateurs (`pending`, `storageState`). Même visibilité que
   * `interventions.list` (un mécanicien : ses dossiers et ceux sans mécanicien).
   */
  parc: z
    .array(z.object({ status: z.enum(WORKSHOP_STATUS_FLOW), interventions: z.array(InterventionSummary) }))
    .optional(),
  /** Vague 6 (ajout 11) : « Arrivées prévues ». */
  plannedArrivals: HomePlannedArrivals.optional(),
});

/** Valeur d'un changement du journal, déjà résolue en libellé lisible par le serveur (vague 4). */
export const ActivityValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type ActivityValue = z.infer<typeof ActivityValue>;

/** Un champ modifié : `field` est une clé de `ACTIVITY_FIELD_LABELS`. Dates en ISO 8601. */
export const ActivityChange = z.object({ field: z.string(), from: ActivityValue, to: ActivityValue });
export type ActivityChange = z.infer<typeof ActivityChange>;

/**
 * Nature de l'auteur, pour la couleur et le libellé à l'affichage (vague 4) :
 * `user` → garage ; `portal` → customer (lien partagé compris) ; `system` → automatic ; `platform` → platform.
 */
export const ActorKind = z.enum(['garage', 'customer', 'automatic', 'platform']);
export type ActorKind = z.infer<typeof ActorKind>;

export const ActivityEntry = z.object({
  id: Id,
  action: z.string(),
  actorType: ActorType,
  actorKind: ActorKind,
  actorName: z.string().nullable(),
  entityType: z.string(),
  entityId: Id.nullable(),
  createdAt: Timestamp,
  /**
   * Champs modifiés (liste blanche, montants retirés sans `amounts.view`, jamais d'IP, de jeton ni
   * d'identifiant technique). Vide pour une entrée ancienne ou une action sans changement de champ.
   */
  changes: z.array(ActivityChange),
  /**
   * Complément court produit par le serveur : titre et version d'un complément, nombre de photos,
   * destinataire masqué d'un lien, extrait de 80 caractères d'un message, motif, « via le lien partagé par … ».
   */
  summary: z.string().nullable(),
});
export type ActivityEntry = z.infer<typeof ActivityEntry>;

/** Catégories de filtre du journal (`ACTIVITY_CATEGORY_LABELS`, `ACTION_CATEGORY`). */
export const ActivityCategory = z.enum([
  'dossier',
  'vehicle_customer',
  'complements',
  'photos',
  'messages',
  'links_emails',
  'release_storage',
]);
export type ActivityCategory = z.infer<typeof ActivityCategory>;


/**
 * Filtres du journal (vague 4), appliqués côté serveur. `from`/`to` : jours du fuseau du garage,
 * bornes incluses. `membershipId` étranger au garage : liste vide (jamais 403/404).
 * `cursor` : curseur opaque renvoyé par la page précédente (`nextCursor`).
 */
export const ActivityQuery = z.strictObject({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  actorKind: queryList(ActorKind, 4).optional(),
  membershipId: Id.optional(),
  category: queryList(ActivityCategory, 7).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Page du journal : `total` = nombre d'entrées pour le filtre courant (toutes pages). */
export const ActivityPage = z.object({
  items: z.array(ActivityEntry),
  nextCursor: z.string().nullable(),
  total: z.int(),
});
export type ActivityPage = z.infer<typeof ActivityPage>;
